import React, { useState, useEffect, useMemo } from 'react';
import { teamDirectoryAPI } from '../../services/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Search, Download, Filter, Users, Building2, MapPin, Briefcase,
  ChevronUp, ChevronDown, Instagram, Twitter, Globe, User,
  Mail, Phone, Calendar, Tag, MoreHorizontal, Eye, X
} from 'lucide-react';

// Employment type colors
const EMPLOYMENT_COLORS = {
  'Full-Time': { bg: '#DBEAFE', text: '#1E40AF' },
  'Part-Time': { bg: '#E0E7FF', text: '#3730A3' },
  'Contractor': { bg: '#FEF3C7', text: '#92400E' },
  'Volunteer': { bg: '#D1FAE5', text: '#065F46' },
  'Advisor': { bg: '#F3E8FF', text: '#6B21A8' }
};

// Seniority colors
const SENIORITY_COLORS = {
  'Board': { bg: '#FEE2E2', text: '#991B1B' },
  'C-Suite': { bg: '#FFEDD5', text: '#C2410C' },
  'Director': { bg: '#FEF3C7', text: '#B45309' },
  'Manager': { bg: '#DBEAFE', text: '#1D4ED8' },
  'Lead': { bg: '#D1FAE5', text: '#047857' },
  'Staff': { bg: '#F3F4F6', text: '#374151' },
  'Coordinator': { bg: '#E0E7FF', text: '#4338CA' },
  'KeyMember': { bg: '#FCE7F3', text: '#BE185D' }
};

// Scope type labels
const SCOPE_LABELS = {
  'national': 'National',
  'regional': 'Regional',
  'unique_ip': 'UNIQUE IP'
};

const TeamDirectoryTab = () => {
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [employmentFilter, setEmploymentFilter] = useState('all');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [seniorityFilter, setSeniorityFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'lastName', direction: 'asc' });
  const [selectedMember, setSelectedMember] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Fetch team directory data
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [membersRes, statsRes] = await Promise.all([
        teamDirectoryAPI.getAll({ limit: 500 }),
        teamDirectoryAPI.getStats()
      ]);
      setMembers(membersRes.data.members || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching team directory:', error);
      toast.error('Failed to load team directory');
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort members
  const filteredMembers = useMemo(() => {
    let result = [...members];
    
    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(m => 
        m.firstName?.toLowerCase().includes(query) ||
        m.lastName?.toLowerCase().includes(query) ||
        m.displayName?.toLowerCase().includes(query) ||
        m.email?.toLowerCase().includes(query) ||
        m.primaryRoleTitle?.toLowerCase().includes(query)
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(m => m.status === statusFilter);
    }
    
    // Apply employment filter
    if (employmentFilter !== 'all') {
      result = result.filter(m => m.employmentType === employmentFilter);
    }
    
    // Apply scope filter
    if (scopeFilter !== 'all') {
      result = result.filter(m => 
        m.roleAssignments?.some(r => r.scopeType === scopeFilter)
      );
    }
    
    // Apply seniority filter
    if (seniorityFilter !== 'all') {
      result = result.filter(m => 
        m.roleAssignments?.some(r => r.seniority === seniorityFilter)
      );
    }
    
    // Apply sort
    result.sort((a, b) => {
      let aVal = a[sortConfig.key] || '';
      let bVal = b[sortConfig.key] || '';
      
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [members, searchQuery, statusFilter, employmentFilter, scopeFilter, seniorityFilter, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleExportCSV = () => {
    const headers = [
      'First Name', 'Last Name', 'Email', 'Phone', 'City', 'State',
      'Employment Type', 'Status', 'Primary Role', 'Scope', 'Department', 'Seniority',
      'Skills', 'Instagram', 'Twitter', 'Followers'
    ];
    
    const rows = filteredMembers.map(m => [
      m.firstName,
      m.lastName,
      m.email,
      m.phone || '',
      m.city || '',
      m.state || '',
      m.employmentType,
      m.status,
      m.primaryRoleTitle || '',
      m.primaryScopeType || '',
      m.primaryDepartment || '',
      m.primarySeniority || '',
      (m.skills || []).join('; '),
      m.socialMedia?.instagramHandle || '',
      m.socialMedia?.twitterHandle || '',
      m.socialMedia?.followerCount || ''
    ]);
    
    const csv = [headers, ...rows].map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'internal-team-directory.csv';
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success('Team directory exported successfully');
  };

  const openMemberDetail = (member) => {
    setSelectedMember(member);
    setDetailOpen(true);
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="w-3 h-3 inline ml-1" />
      : <ChevronDown className="w-3 h-3 inline ml-1" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 rounded-xl" style={{ backgroundColor: '#F9FAFB' }}>
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" 
          style={{ borderColor: '#2A9D8F', borderTopColor: 'transparent' }} 
        />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="team-directory-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: '#264653' }}>Internal Team Directory</h2>
          <p className="text-sm" style={{ color: '#6B7280' }}>
            {stats?.total || 0} team members across all organizational charts
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={handleExportCSV}
          data-testid="export-team-csv-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <div className="p-3 rounded-lg bg-white border" style={{ borderColor: '#E5E7EB' }}>
          <p className="text-xs" style={{ color: '#6B7280' }}>Total</p>
          <p className="text-xl font-semibold" style={{ color: '#264653' }}>{stats?.total || 0}</p>
        </div>
        <div className="p-3 rounded-lg bg-white border" style={{ borderColor: '#E5E7EB' }}>
          <p className="text-xs" style={{ color: '#6B7280' }}>Active</p>
          <p className="text-xl font-semibold" style={{ color: '#10B981' }}>{stats?.active || 0}</p>
        </div>
        <div className="p-3 rounded-lg bg-white border" style={{ borderColor: '#E5E7EB' }}>
          <p className="text-xs" style={{ color: '#6B7280' }}>Full-Time</p>
          <p className="text-xl font-semibold" style={{ color: '#1E40AF' }}>
            {stats?.byEmploymentType?.['Full-Time'] || 0}
          </p>
        </div>
        <div className="p-3 rounded-lg bg-white border" style={{ borderColor: '#E5E7EB' }}>
          <p className="text-xs" style={{ color: '#6B7280' }}>Part-Time</p>
          <p className="text-xl font-semibold" style={{ color: '#3730A3' }}>
            {stats?.byEmploymentType?.['Part-Time'] || 0}
          </p>
        </div>
        <div className="p-3 rounded-lg bg-white border" style={{ borderColor: '#E5E7EB' }}>
          <p className="text-xs" style={{ color: '#6B7280' }}>Contractors</p>
          <p className="text-xl font-semibold" style={{ color: '#92400E' }}>
            {stats?.byEmploymentType?.['Contractor'] || 0}
          </p>
        </div>
        <div className="p-3 rounded-lg bg-white border" style={{ borderColor: '#E5E7EB' }}>
          <p className="text-xs" style={{ color: '#6B7280' }}>Volunteers</p>
          <p className="text-xl font-semibold" style={{ color: '#065F46' }}>
            {stats?.byEmploymentType?.['Volunteer'] || 0}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#9CA3AF' }} />
          <Input
            placeholder="Search by name, email, role..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="team-search-input"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[130px]" data-testid="team-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={employmentFilter} onValueChange={setEmploymentFilter}>
          <SelectTrigger className="w-[140px]" data-testid="team-employment-filter">
            <SelectValue placeholder="Employment" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="Full-Time">Full-Time</SelectItem>
            <SelectItem value="Part-Time">Part-Time</SelectItem>
            <SelectItem value="Contractor">Contractor</SelectItem>
            <SelectItem value="Volunteer">Volunteer</SelectItem>
            <SelectItem value="Advisor">Advisor</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={scopeFilter} onValueChange={setScopeFilter}>
          <SelectTrigger className="w-[130px]" data-testid="team-scope-filter">
            <SelectValue placeholder="Scope" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Scopes</SelectItem>
            <SelectItem value="national">National</SelectItem>
            <SelectItem value="regional">Regional</SelectItem>
            <SelectItem value="unique_ip">UNIQUE IP</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={seniorityFilter} onValueChange={setSeniorityFilter}>
          <SelectTrigger className="w-[130px]" data-testid="team-seniority-filter">
            <SelectValue placeholder="Seniority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Levels</SelectItem>
            <SelectItem value="Board">Board</SelectItem>
            <SelectItem value="C-Suite">C-Suite</SelectItem>
            <SelectItem value="Director">Director</SelectItem>
            <SelectItem value="Manager">Manager</SelectItem>
            <SelectItem value="Lead">Lead</SelectItem>
            <SelectItem value="Staff">Staff</SelectItem>
            <SelectItem value="Coordinator">Coordinator</SelectItem>
            <SelectItem value="KeyMember">Key Member</SelectItem>
          </SelectContent>
        </Select>
        
        <div className="text-sm" style={{ color: '#6B7280' }}>
          Showing {filteredMembers.length} of {members.length}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg overflow-hidden bg-white border" style={{ borderColor: '#E5E7EB' }}>
        <Table>
          <TableHeader>
            <TableRow style={{ backgroundColor: '#F9FAFB' }}>
              <TableHead 
                className="cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('lastName')}
              >
                Name <SortIcon columnKey="lastName" />
              </TableHead>
              <TableHead>Contact</TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('employmentType')}
              >
                Employment <SortIcon columnKey="employmentType" />
              </TableHead>
              <TableHead>Primary Role</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Skills</TableHead>
              <TableHead>Social</TableHead>
              <TableHead className="w-[60px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredMembers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8" style={{ color: '#6B7280' }}>
                  No team members found matching your filters
                </TableCell>
              </TableRow>
            ) : (
              filteredMembers.map(member => (
                <TableRow 
                  key={member.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => openMemberDetail(member)}
                  data-testid={`team-row-${member.id}`}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-medium"
                        style={{ backgroundColor: '#E8F4F2', color: '#2A9D8F' }}
                      >
                        {member.firstName?.[0]}{member.lastName?.[0]}
                      </div>
                      <div>
                        <div className="font-medium" style={{ color: '#1F2937' }}>
                          {member.displayName}
                        </div>
                        <div className="text-xs" style={{ color: '#6B7280' }}>
                          {member.city && member.state ? `${member.city}, ${member.state}` : ''}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-xs space-y-1">
                      <div className="flex items-center gap-1" style={{ color: '#374151' }}>
                        <Mail className="w-3 h-3" style={{ color: '#9CA3AF' }} />
                        {member.email}
                      </div>
                      {member.phone && (
                        <div className="flex items-center gap-1" style={{ color: '#6B7280' }}>
                          <Phone className="w-3 h-3" style={{ color: '#9CA3AF' }} />
                          {member.phone}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <Badge 
                        style={{ 
                          backgroundColor: EMPLOYMENT_COLORS[member.employmentType]?.bg || '#F3F4F6',
                          color: EMPLOYMENT_COLORS[member.employmentType]?.text || '#374151'
                        }}
                      >
                        {member.employmentType}
                      </Badge>
                      <div>
                        <Badge 
                          variant="outline"
                          style={{ 
                            borderColor: member.status === 'Active' ? '#10B981' : '#EF4444',
                            color: member.status === 'Active' ? '#10B981' : '#EF4444'
                          }}
                        >
                          {member.status}
                        </Badge>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="text-sm font-medium" style={{ color: '#1F2937' }}>
                        {member.primaryRoleTitle || '-'}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge 
                          className="text-xs"
                          style={{ 
                            backgroundColor: SENIORITY_COLORS[member.primarySeniority]?.bg || '#F3F4F6',
                            color: SENIORITY_COLORS[member.primarySeniority]?.text || '#374151'
                          }}
                        >
                          {member.primarySeniority}
                        </Badge>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {member.roleAssignments?.slice(0, 2).map((r, i) => (
                        <Badge key={i} variant="outline" className="text-xs mr-1">
                          {SCOPE_LABELS[r.scopeType] || r.scopeType}
                          {r.scopeType === 'regional' && ` ${r.scopeId?.split('_')[1]}`}
                        </Badge>
                      ))}
                      {member.roleAssignments?.length > 2 && (
                        <Badge variant="secondary" className="text-xs">
                          +{member.roleAssignments.length - 2}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1 max-w-[150px]">
                      {member.skills?.slice(0, 2).map((skill, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {skill}
                        </Badge>
                      ))}
                      {member.skills?.length > 2 && (
                        <Badge variant="secondary" className="text-xs">
                          +{member.skills.length - 2}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {member.socialMedia && (
                      <div className="flex items-center gap-2">
                        {member.socialMedia.instagramHandle && (
                          <Instagram className="w-4 h-4" style={{ color: '#E4405F' }} />
                        )}
                        {member.socialMedia.twitterHandle && (
                          <Twitter className="w-4 h-4" style={{ color: '#1DA1F2' }} />
                        )}
                        {member.socialMedia.followerCount > 0 && (
                          <span className="text-xs" style={{ color: '#6B7280' }}>
                            {member.socialMedia.followerCount > 1000 
                              ? `${(member.socialMedia.followerCount / 1000).toFixed(1)}k`
                              : member.socialMedia.followerCount
                            }
                          </span>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 w-8 p-0"
                      onClick={() => openMemberDetail(member)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Member Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-medium"
                style={{ backgroundColor: '#E8F4F2', color: '#2A9D8F' }}
              >
                {selectedMember?.firstName?.[0]}{selectedMember?.lastName?.[0]}
              </div>
              <div>
                <div style={{ color: '#264653' }}>{selectedMember?.displayName}</div>
                <div className="text-sm font-normal" style={{ color: '#6B7280' }}>
                  {selectedMember?.primaryRoleTitle}
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>

          {selectedMember && (
            <Tabs defaultValue="info" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="info">Info</TabsTrigger>
                <TabsTrigger value="roles">Roles ({selectedMember.roleAssignments?.length || 0})</TabsTrigger>
                <TabsTrigger value="skills">Skills</TabsTrigger>
                <TabsTrigger value="social">Social</TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium" style={{ color: '#6B7280' }}>Email</label>
                    <p style={{ color: '#1F2937' }}>{selectedMember.email}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium" style={{ color: '#6B7280' }}>Phone</label>
                    <p style={{ color: '#1F2937' }}>{selectedMember.phone || '-'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium" style={{ color: '#6B7280' }}>Secondary Phone</label>
                    <p style={{ color: '#1F2937' }}>{selectedMember.secondaryPhone || '-'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium" style={{ color: '#6B7280' }}>Location</label>
                    <p style={{ color: '#1F2937' }}>
                      {selectedMember.city && selectedMember.state 
                        ? `${selectedMember.city}, ${selectedMember.state}` 
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium" style={{ color: '#6B7280' }}>Employment Type</label>
                    <p>
                      <Badge style={{ 
                        backgroundColor: EMPLOYMENT_COLORS[selectedMember.employmentType]?.bg,
                        color: EMPLOYMENT_COLORS[selectedMember.employmentType]?.text
                      }}>
                        {selectedMember.employmentType}
                      </Badge>
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium" style={{ color: '#6B7280' }}>Status</label>
                    <p>
                      <Badge variant="outline" style={{ 
                        borderColor: selectedMember.status === 'Active' ? '#10B981' : '#EF4444',
                        color: selectedMember.status === 'Active' ? '#10B981' : '#EF4444'
                      }}>
                        {selectedMember.status}
                      </Badge>
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium" style={{ color: '#6B7280' }}>Start Date</label>
                    <p style={{ color: '#1F2937' }}>{selectedMember.startDate || '-'}</p>
                  </div>
                </div>
                {selectedMember.notes && (
                  <div>
                    <label className="text-xs font-medium" style={{ color: '#6B7280' }}>Notes</label>
                    <p className="text-sm mt-1 p-2 rounded" style={{ backgroundColor: '#F9FAFB', color: '#374151' }}>
                      {selectedMember.notes}
                    </p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="roles" className="space-y-3 pt-4">
                {selectedMember.roleAssignments?.map((role, idx) => (
                  <div 
                    key={idx}
                    className="p-3 rounded-lg border"
                    style={{ borderColor: role.isPrimary ? '#2A9D8F' : '#E5E7EB' }}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-medium" style={{ color: '#1F2937' }}>
                          {role.roleTitle}
                          {role.isPrimary && (
                            <Badge className="ml-2" style={{ backgroundColor: '#2A9D8F', color: 'white' }}>
                              Primary
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm mt-1" style={{ color: '#6B7280' }}>
                          {role.department}
                        </div>
                      </div>
                      <Badge style={{ 
                        backgroundColor: SENIORITY_COLORS[role.seniority]?.bg,
                        color: SENIORITY_COLORS[role.seniority]?.text
                      }}>
                        {role.seniority}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs" style={{ color: '#6B7280' }}>
                      <span>Scope: <strong>{SCOPE_LABELS[role.scopeType]}</strong></span>
                      <span>ID: <strong>{role.scopeId}</strong></span>
                      {role.reportsTo && <span>Reports to: <strong>{role.reportsTo}</strong></span>}
                    </div>
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="skills" className="pt-4">
                <div className="flex flex-wrap gap-2">
                  {selectedMember.skills?.length > 0 ? (
                    selectedMember.skills.map((skill, idx) => (
                      <Badge key={idx} variant="secondary" className="px-3 py-1">
                        {skill}
                      </Badge>
                    ))
                  ) : (
                    <p style={{ color: '#6B7280' }}>No skills listed</p>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="social" className="space-y-4 pt-4">
                {selectedMember.socialMedia ? (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedMember.socialMedia.instagramHandle && (
                        <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: '#FDF2F8' }}>
                          <Instagram className="w-5 h-5" style={{ color: '#E4405F' }} />
                          <div>
                            <p className="text-sm font-medium">@{selectedMember.socialMedia.instagramHandle}</p>
                            <a 
                              href={selectedMember.socialMedia.instagramUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs hover:underline"
                              style={{ color: '#E4405F' }}
                            >
                              View Profile
                            </a>
                          </div>
                        </div>
                      )}
                      {selectedMember.socialMedia.twitterHandle && (
                        <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: '#EFF6FF' }}>
                          <Twitter className="w-5 h-5" style={{ color: '#1DA1F2' }} />
                          <div>
                            <p className="text-sm font-medium">@{selectedMember.socialMedia.twitterHandle}</p>
                            <a 
                              href={selectedMember.socialMedia.twitterUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs hover:underline"
                              style={{ color: '#1DA1F2' }}
                            >
                              View Profile
                            </a>
                          </div>
                        </div>
                      )}
                      {selectedMember.socialMedia.tiktokHandle && (
                        <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: '#F0FDF4' }}>
                          <Globe className="w-5 h-5" style={{ color: '#000' }} />
                          <div>
                            <p className="text-sm font-medium">@{selectedMember.socialMedia.tiktokHandle}</p>
                            <a 
                              href={selectedMember.socialMedia.tiktokUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs hover:underline"
                              style={{ color: '#000' }}
                            >
                              View TikTok
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-4 pt-4 border-t" style={{ borderColor: '#E5E7EB' }}>
                      <div className="text-center">
                        <p className="text-2xl font-bold" style={{ color: '#264653' }}>
                          {selectedMember.socialMedia.followerCount?.toLocaleString() || 0}
                        </p>
                        <p className="text-xs" style={{ color: '#6B7280' }}>Followers</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium" style={{ color: '#264653' }}>
                          {selectedMember.socialMedia.socialInfluenceLevel || '-'}
                        </p>
                        <p className="text-xs" style={{ color: '#6B7280' }}>Influence Level</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium" style={{ color: '#264653' }}>
                          {selectedMember.socialMedia.contentStrength || '-'}
                        </p>
                        <p className="text-xs" style={{ color: '#6B7280' }}>Content Strength</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <p style={{ color: '#6B7280' }}>No social media information</p>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamDirectoryTab;
